import { useState } from "react";
import { Link } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";

const FORM_ENDPOINT = "https://readdy.ai/api/form/db8b4j1rh5tg6socjscg";

interface ParsedResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

export default function About() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [statusMessage, setStatusMessage] = useState("");

  usePageMeta(
    "关于我们 - 91视频在线观看高清影视免费在线观看平台",
    "了解91视频在线观看的平台定位、服务特色与联系方式。我们致力于为影视爱好者提供便捷、丰富的在线观看与浏览体验。",
    "91视频在线观看,关于我们,影视平台,在线观看平台,联系我们",
  );

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setStatusMessage("提交成功，感谢你的留言，我们会尽快回复你。");
      form.reset();
      return;
    }
    formData.delete("website_alt");

    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === "string") body.append(key, value);
    });

    setStatus("submitting");
    setStatusMessage("");

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: ParsedResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as ParsedResponse;
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const code = parsed?.code;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && code === "OK" && !isSpam) {
        setStatus("success");
        setStatusMessage("提交成功，感谢你的留言，我们会尽快回复你。");
        form.reset();
      } else {
        setStatus("error");
        setStatusMessage(serverMsg || "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setStatusMessage("网络异常，提交失败，请检查网络后重试。");
    }
  };

  const features = [
    {
      icon: "ri-film-line",
      title: "内容覆盖全面",
      desc: "汇聚电影、电视剧、综艺三大频道，持续更新最新热门影视资源。",
    },
    {
      icon: "ri-search-eye-line",
      title: "精准搜索筛选",
      desc: "支持标题、类型、地区、演员多维搜索，并可按地区年份快速筛选。",
    },
    {
      icon: "ri-device-line",
      title: "多终端体验",
      desc: "电脑与手机自适应访问，随时随地浏览想看的影视内容。",
    },
  ];

  return (
    <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-8 md:py-12">
      <nav className="flex items-center gap-2 text-xs text-foreground-500">
        <Link to="/" className="transition hover:text-primary-400">首页</Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-800">关于我们</span>
      </nav>

      <header className="mt-5 max-w-3xl">
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
          关于 91视频在线观看
        </h1>
        <p className="mt-4 text-sm leading-7 text-foreground-700 md:text-base">
          91视频在线观看是一家面向广大影视爱好者的在线视频平台，专注于影视资源的展示与浏览体验。我们为喜欢在电脑和手机多终端观看影视内容的用户，提供电影、电视剧、综艺等高清资源的分类浏览、精准搜索与详情查看服务，让找片变得简单又愉快。
        </p>
      </header>

      <section className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {features.map((f) => (
          <div
            key={f.title}
            className="rounded-lg border border-background-200/70 bg-background-100 p-6"
          >
            <div className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-500/15 text-primary-500">
              <i className={`${f.icon} text-xl`}></i>
            </div>
            <h2 className="mt-4 font-heading text-base font-bold text-foreground-950">{f.title}</h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">{f.desc}</p>
          </div>
        ))}
      </section>

      <section className="mt-12 grid grid-cols-1 gap-8 rounded-lg border border-background-200/70 bg-background-100 p-6 md:p-8 lg:grid-cols-2">
        <div>
          <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            我们的定位
          </h2>
          <p className="mt-3 text-sm leading-7 text-foreground-700">
            网站内容与功能聚焦影视资源展示与在线观看演示，侧重用户的观看便利与内容丰富度。我们相信，好的影视平台不只是内容的堆积，更是顺手的浏览、清晰的信息与贴心的推荐。
          </p>
          <ul className="mt-5 flex flex-col gap-3 text-sm text-foreground-700">
            <li className="flex items-start gap-2">
              <i className="ri-check-line mt-0.5 text-primary-500"></i>
              聚焦影视资源展示与在线观看演示体验
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-check-line mt-0.5 text-primary-500"></i>
              以分类、搜索、筛选与推荐为核心，帮助用户快速找到想看的影片
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-check-line mt-0.5 text-primary-500"></i>
              页面清晰、信息完整，兼顾电脑与手机多终端使用
            </li>
          </ul>
        </div>
        <div className="flex flex-col justify-center gap-4">
          <div className="rounded-lg border border-background-200/70 bg-secondary-950 p-5">
            <div className="flex items-center gap-2 text-sm font-medium text-foreground-900">
              <i className="ri-mail-line text-primary-500"></i>
              联系邮箱
            </div>
            <p className="mt-2 text-sm text-foreground-600">service@example-91video.com</p>
          </div>
          <div className="rounded-lg border border-background-200/70 bg-secondary-950 p-5">
            <div className="flex items-center gap-2 text-sm font-medium text-foreground-900">
              <i className="ri-map-pin-line text-primary-500"></i>
              办公地址
            </div>
            <p className="mt-2 text-sm text-foreground-600">中国 · 北京市朝阳区影视创意产业园 B 座</p>
          </div>
          <div className="rounded-lg border border-background-200/70 bg-secondary-950 p-5">
            <div className="flex items-center gap-2 text-sm font-medium text-foreground-900">
              <i className="ri-time-line text-primary-500"></i>
              服务时间
            </div>
            <p className="mt-2 text-sm text-foreground-600">周一至周五 09:30 - 18:30</p>
          </div>
        </div>
      </section>

      <section id="contact" className="mt-12 scroll-mt-24">
        <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
          联系我们
        </h2>
        <p className="mt-2 text-sm text-foreground-600">
          有任何建议、合作意向或问题反馈，欢迎填写下面的表单，我们会尽快与你联系。
        </p>

        <form
          id="contact-form"
          data-readdy-form
          onSubmit={handleSubmit}
          className="mt-6 grid grid-cols-1 gap-5 rounded-lg border border-background-200/70 bg-background-100 p-6 md:grid-cols-2 md:p-8"
        >
          <div className="flex flex-col gap-2">
            <label htmlFor="cf-name" className="text-sm font-medium text-foreground-800">
              姓名 <span className="text-primary-500">*</span>
            </label>
            <input
              id="cf-name"
              name="name"
              type="text"
              required
              placeholder="请输入你的称呼"
              className="h-11 rounded-md border border-background-200 bg-background-50 px-3 text-sm text-foreground-950 outline-none transition focus:border-primary-400 placeholder:text-foreground-500"
            />
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="cf-email" className="text-sm font-medium text-foreground-800">
              邮箱 <span className="text-primary-500">*</span>
            </label>
            <input
              id="cf-email"
              name="email"
              type="email"
              required
              placeholder="请输入常用邮箱"
              className="h-11 rounded-md border border-background-200 bg-background-50 px-3 text-sm text-foreground-950 outline-none transition focus:border-primary-400 placeholder:text-foreground-500"
            />
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="cf-phone" className="text-sm font-medium text-foreground-800">
              联系电话
            </label>
            <input
              id="cf-phone"
              name="phone"
              type="tel"
              placeholder="选填，方便我们电话回复"
              className="h-11 rounded-md border border-background-200 bg-background-50 px-3 text-sm text-foreground-950 outline-none transition focus:border-primary-400 placeholder:text-foreground-500"
            />
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="cf-type" className="text-sm font-medium text-foreground-800">
              咨询类型
            </label>
            <select
              id="cf-type"
              name="subject"
              defaultValue="使用建议"
              className="h-11 rounded-md border border-background-200 bg-background-50 px-3 text-sm text-foreground-950 outline-none transition focus:border-primary-400"
            >
              <option value="使用建议">使用建议</option>
              <option value="内容反馈">内容反馈</option>
              <option value="商务合作">商务合作</option>
              <option value="其他问题">其他问题</option>
            </select>
          </div>

          <div className="flex flex-col gap-2 md:col-span-2">
            <label htmlFor="cf-message" className="text-sm font-medium text-foreground-800">
              留言内容 <span className="text-primary-500">*</span>
            </label>
            <textarea
              id="cf-message"
              name="message"
              required
              maxLength={500}
              rows={5}
              placeholder="请描述你的建议或问题（最多 500 字）"
              className="resize-none rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 placeholder:text-foreground-500"
            />
          </div>

          <input
            className="form-hp-field"
            type="text"
            name="website_alt"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            readOnly
          />

          <div className="flex flex-col gap-3 md:col-span-2 md:flex-row md:items-center md:justify-between">
            <p className="text-xs text-foreground-500">
              提交即表示你已阅读并同意本站的隐私与法律声明。
            </p>
            <button
              type="submit"
              disabled={status === "submitting"}
              className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-7 py-3 text-sm font-medium text-background-50 transition hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <i className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"}></i>
              {status === "submitting" ? "提交中…" : "提交留言"}
            </button>
          </div>

          {status === "success" && (
            <div className="flex items-center gap-2 rounded-md border border-primary-300/60 bg-primary-100/70 px-4 py-3 text-sm text-primary-900 md:col-span-2">
              <i className="ri-checkbox-circle-line"></i>
              {statusMessage}
            </div>
          )}
          {status === "error" && (
            <div className="flex items-center gap-2 rounded-md border border-primary-400/60 bg-background-200 px-4 py-3 text-sm text-foreground-900 md:col-span-2">
              <i className="ri-error-warning-line text-primary-500"></i>
              {statusMessage}
            </div>
          )}
        </form>
      </section>

      <section id="legal" className="mt-12 scroll-mt-24">
        <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
          法律声明与版权信息
        </h2>
        <div className="mt-4 flex flex-col gap-4 rounded-lg border border-background-200/70 bg-background-100 p-6 text-sm leading-7 text-foreground-700">
          <p>
            1. 本站为影视资源展示与在线观看演示平台，页面内的影片信息、评分、播放画面等均为演示示例，不构成对任何真实影视作品的引用或授权。
          </p>
          <p>
            2. 播放区域为前端模拟界面，不提供任何真实视频流播放服务，也不存储、上传或分发任何影视资源文件。
          </p>
          <p>
            3. 用户通过本站进行的收藏操作仅保存在本地浏览器中，不会上传到服务器，也不会与其他设备或账号同步。
          </p>
          <p>
            4. 本站尊重知识产权。如权利人对演示内容有异议，可通过上方联系方式与我们取得联系，我们将及时处理。
          </p>
          <p className="text-foreground-500">
            © {new Date().getFullYear()} 91视频在线观看 · 保留所有权利 · 本站内容仅供产品展示与演示使用。
          </p>
        </div>
      </section>
    </div>
  );
}